import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Word, Pairing, PairingStatus, PairingRating, ClueStatus, CrosswordData, FilterState, ListSide, ConstructorGrid, GridPlacement, ViewMode, GridDraft, ScratchpadEntry, GridFragment } from './types'
import { generateId, createPairing, getLettersFromText, getLetters, splitCrossListGroups } from './lib/pairings'
import {
  searchWordsWithWildcard,
  findByLetterPositions,
  filterByPairingStatus,
  filterByClueStatus,
  searchByClueText
} from './lib/search'
import {
  createGrid,
  placePair,
  removePlacement,
  removePlacements,
  resizeGrid,
  clearGrid,
  toggleCellBlocked,
  toggleCellsBlocked,
  movePlacement,
  movePlacements,
  rotatePlacement,
  updatePlacementLetter,
  validatePlacementBatch
} from './lib/constructor'
import type { PlacePairInput } from './lib/constructor'
import { fragmentFromPlacements, tryApplyFragment } from './lib/fragments'
import { sanitizeCrosswordData } from './lib/data'

type CrosswordStore = {
  // Data
  words: Word[]
  pairings: Pairing[]
  listNames: { A: string; B: string }

  // UI State
  selectedWordId: string | null
  selectedPairingId: string | null
  activeView: 'words' | 'explorer' | 'crossword' | 'search' | 'clues'
  filters: FilterState

  // Constructor State
  constructorGrid: ConstructorGrid | null
  constructorViewMode: ViewMode
  selectedGridCell: { row: number; col: number } | null
  draggedPairingId: string | null
  gridVersion: number

  // Drafts State
  drafts: GridDraft[]
  activeDraftId: string | null
  historyPast: HistorySnapshot[]
  historyFuture: HistorySnapshot[]
  canUndo: boolean
  canRedo: boolean

  // Scratchpad
  scratchpadEntries: ScratchpadEntry[]
  scratchpadGrid: ConstructorGrid | null

  // Fragments (reusable saved groups of placements)
  fragments: GridFragment[]

  // Tag registry — every label that exists or has been registered. Words /
  // pairings reference tags by label; the registry exists so the filter UI
  // and autocomplete have a known set without scanning every entity.
  tags: string[]

  // Word Actions
  addWord: (text: string, list?: ListSide, groupId?: string) => void
  addWords: (texts: string[], list?: ListSide) => void
  addVariant: (wordId: string, variantText: string) => void
  updateWord: (id: string, updates: Partial<Pick<Word, 'text' | 'groupId'>>) => void
  assignWordToList: (id: string, list: ListSide | undefined) => void
  deleteWord: (id: string) => void
  createWordGroup: (wordIds: string[]) => string
  removeFromGroup: (wordId: string) => void
  markAsTwins: (wordIdA: string, wordIdB: string) => void
  unmarkTwin: (wordId: string) => void
  setWordTags: (wordId: string, tags: string[]) => void

  // Pairing Actions
  getOrCreatePairing: (wordAId: string, wordBId: string) => Pairing | null
  updatePairingStatus: (id: string, status: PairingStatus) => void
  updatePairingRating: (id: string, rating: PairingRating | null) => void
  updatePairingClue: (id: string, text: string, status: ClueStatus) => void
  addPairingDraftClue: (id: string, text: string) => void
  deletePairingDraftClue: (pairingId: string, clueId: string) => void
  deletePairing: (id: string) => void
  setPairingTags: (pairingId: string, tags: string[]) => void

  // Tag Registry Actions
  registerTag: (label: string) => void
  renameTag: (oldLabel: string, newLabel: string) => void
  deleteTag: (label: string) => void

  // UI Actions
  setSelectedWord: (id: string | null) => void
  setSelectedPairing: (id: string | null) => void
  setActiveView: (view: CrosswordStore['activeView']) => void
  setFilters: (filters: Partial<FilterState>) => void
  resetFilters: () => void
  setListName: (list: 'A' | 'B', name: string) => void

  // Constructor Actions
  undo: () => void
  redo: () => void
  createConstructorGrid: (width: number, height: number) => void
  placeOnGrid: (placement: Omit<GridPlacement, 'id'>) => { success: boolean; error?: string; placementId?: string }
  // Atomic batch placement: validates and applies all placements in one undo
  // step. Used by the backwards-search "Apply solution" flow to land helpers
  // and the target as a single action.
  placeManyOnGrid: (placements: PlacePairInput[]) => { success: boolean; error?: string; placementIds?: string[] }
  removeFromGrid: (placementId: string) => void
  removeManyFromGrid: (placementIds: readonly string[]) => void
  // Move a group of placements from the main grid onto the scratchpad as a
  // single undo step. The scratchpad is auto-created/resized to fit.
  sendPlacementsToScratchpad: (placementIds: readonly string[]) => { success: boolean; error?: string }
  moveGridPlacement: (placementId: string, newRow: number, newCol: number) => void
  moveGridPlacements: (updates: Array<{ placementId: string; newRow: number; newCol: number }>) => void
  rotateGridPlacement: (placementId: string) => void
  resizeConstructorGrid: (width: number, height: number, rowOffset?: number, colOffset?: number) => void
  clearConstructorGrid: () => void
  toggleGridCellBlocked: (row: number, col: number) => void
  toggleGridCellsBlocked: (cells: Array<{ row: number; col: number }>) => void
  setConstructorViewMode: (mode: ViewMode) => void
  setSelectedGridCell: (cell: { row: number; col: number } | null) => void
  setDraggedPairingId: (id: string | null) => void
  saveManualEntry: (wordAText: string, wordBText: string, clue?: string) => Pairing | null
  updateGridPlacementLetter: (placementId: string, letterIndex: number, newLetter: string, side: 'A' | 'B') => void
  saveTempWord: (placementId: string, side: 'A' | 'B') => void
  setPlacementPriority: (placementId: string, priority: number | undefined) => void

  // Draft Actions
  createDraft: (name?: string) => void
  switchDraft: (draftId: string) => void
  renameDraft: (draftId: string, name: string) => void
  deleteDraft: (draftId: string) => void
  duplicateDraft: (draftId: string) => void

  // Scratchpad Actions
  addScratchpadEntry: () => void
  updateScratchpadEntry: (id: string, updates: Partial<Pick<ScratchpadEntry, 'wordA' | 'wordB' | 'clue'>>) => void
  removeScratchpadEntry: (id: string) => void
  createScratchpadGrid: () => void
  placeOnScratchpad: (placement: Omit<GridPlacement, 'id'>) => { success: boolean; error?: string; placementId?: string }
  removeFromScratchpad: (placementId: string) => void
  removeManyFromScratchpad: (placementIds: readonly string[]) => void
  moveScratchpadPlacement: (placementId: string, newRow: number, newCol: number) => void
  moveScratchpadPlacements: (updates: Array<{ placementId: string; newRow: number; newCol: number }>) => void
  rotateScratchpadPlacement: (placementId: string) => void
  clearScratchpadGrid: () => void
  updateScratchpadPlacementLetter: (placementId: string, letterIndex: number, newLetter: string, side: 'A' | 'B') => void

  // Fragment Actions
  saveFragment: (placements: GridPlacement[], name: string) => GridFragment | null
  deleteFragment: (id: string) => void
  renameFragment: (id: string, name: string) => void
  applyFragmentToGrid: (fragmentId: string, rowOffset: number, colOffset: number) => { success: boolean; error?: string }

  // Search/Filter (computed, returns filtered results)
  getFilteredPairings: () => Pairing[]
  searchWords: (pattern: string) => Word[]
  findPairingsByLetterPositions: (positions: Record<number, string>) => Pairing[]

  // Derived state
  getInsidePairingIds: () => Set<string>

  // Export/Import
  exportData: () => CrosswordData
  importData: (data: unknown) => void
  mergeData: (data: unknown) => void
  clearAllData: () => void
}

type HistorySnapshot = {
  words: Word[]
  pairings: Pairing[]
  listNames: { A: string; B: string }
  constructorGrid: ConstructorGrid | null
  drafts: GridDraft[]
  activeDraftId: string | null
  scratchpadGrid: ConstructorGrid | null
}

const defaultFilters: FilterState = {
  pairingStatuses: [],
  clueStatuses: [],
  searchQuery: ''
}

const MAX_HISTORY_ENTRIES = 100

// Tag helpers. Labels are the canonical id, so trim and dedupe on the way in.
// Empty / whitespace-only labels are dropped.
function normalizeTagInput(tags: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of tags) {
    const cleaned = raw.trim()
    if (!cleaned || seen.has(cleaned)) continue
    seen.add(cleaned)
    out.push(cleaned)
  }
  return out
}

function mergeRegistry(existing: string[], incoming: string[]): string[] {
  const seen = new Set(existing)
  let changed = false
  for (const t of incoming) {
    if (!seen.has(t)) { seen.add(t); changed = true }
  }
  if (!changed) return existing
  return Array.from(seen).sort((a, b) => a.localeCompare(b))
}

function replaceLabel(tags: string[], oldLabel: string, newLabel: string): string[] {
  const next: string[] = []
  const seen = new Set<string>()
  for (const t of tags) {
    const replaced = t === oldLabel ? newLabel : t
    if (seen.has(replaced)) continue
    seen.add(replaced)
    next.push(replaced)
  }
  return next
}

export const useCrosswordStore = create<CrosswordStore>()(
  persist(
    (set, get) => {
      // Snapshot by reference — safe because zustand's set() always creates new
      // objects for changed slices, so previous references remain immutable.
      const createHistorySnapshot = (): HistorySnapshot => {
        const { words, pairings, listNames, constructorGrid, drafts, activeDraftId, scratchpadGrid } = get()
        return { words, pairings, listNames, constructorGrid, drafts, activeDraftId, scratchpadGrid }
      }

      const pushHistory = () => {
        const snapshot = createHistorySnapshot()
        set(state => {
          const nextPast = [...state.historyPast, snapshot]
          if (nextPast.length > MAX_HISTORY_ENTRIES) nextPast.shift()
          return {
            historyPast: nextPast,
            historyFuture: [],
            canUndo: nextPast.length > 0,
            canRedo: false
          }
        })
      }

      // Helper: update constructorGrid and sync to active draft atomically
      const setGrid = (
        newGrid: ConstructorGrid | null,
        options?: { recordHistory?: boolean }
      ) => {
        if (options?.recordHistory) {
          pushHistory()
        }
        const { activeDraftId, drafts, gridVersion } = get()
        const updatedAt = new Date().toISOString()
        set({
          constructorGrid: newGrid,
          gridVersion: gridVersion + 1,
          ...(activeDraftId ? {
            drafts: drafts.map(d =>
              d.id === activeDraftId ? { ...d, grid: newGrid ?? d.grid, updatedAt } : d
            )
          } : {})
        })

      }

      const setScratchpadGrid = (
        newGrid: ConstructorGrid | null,
        options?: { recordHistory?: boolean }
      ) => {
        if (options?.recordHistory) {
          pushHistory()
        }
        set({ scratchpadGrid: newGrid })
      }

      return ({
      // Initial State
      words: [],
      pairings: [],
      listNames: { A: 'List A', B: 'List B' },
      selectedWordId: null,
      selectedPairingId: null,
      activeView: 'words',
      filters: defaultFilters,

      // Constructor Initial State
      constructorGrid: null,
      constructorViewMode: 'side-by-side' as ViewMode,
      selectedGridCell: null,
      draggedPairingId: null,
      gridVersion: 0,

      // Drafts Initial State
      drafts: [],
      activeDraftId: null,
      historyPast: [],
      historyFuture: [],
      canUndo: false,
      canRedo: false,

      // Scratchpad
      scratchpadEntries: [],
      scratchpadGrid: null,

      // Fragments
      fragments: [],

      // Tag registry
      tags: [],

      // Word Actions
      addWord: (text, list, groupId) => {
        const normalizedLetters = getLettersFromText(text)

        // Check for duplicate within the same list (same word can exist in both lists)
        const { words } = get()
        const exists = words.some(w =>
          w.list === list &&
          getLettersFromText(w.text) === normalizedLetters
        )
        if (exists) return // Skip duplicate

        const word: Word = {
          id: generateId(),
          text: text.toUpperCase(),
          list,
          groupId
        }
        set(state => ({ words: [...state.words, word] }))
      },

      addWords: (texts, list) => {
        const { words } = get()
        // Dedup is per-list: same text can exist in both lists.
        const existingLetters = new Set(
          words
            .filter(w => w.list === list)
            .map(w => getLettersFromText(w.text))
        )

        const newWords: Word[] = []
        const addedLetters = new Set<string>()

        for (const text of texts) {
          const normalizedLetters = getLettersFromText(text)

          // Skip if already exists in this list or already added in this batch
          if (existingLetters.has(normalizedLetters) || addedLetters.has(normalizedLetters)) {
            continue
          }

          addedLetters.add(normalizedLetters)
          newWords.push({
            id: generateId(),
            text: text.toUpperCase(),
            list
          })
        }

        if (newWords.length > 0) {
          set(state => ({ words: [...state.words, ...newWords] }))
        }
      },

      addVariant: (wordId, variantText) => {
        const { words } = get()
        const originalWord = words.find(w => w.id === wordId)
        if (!originalWord) return

        const normalizedLetters = getLettersFromText(variantText)

        // Check for duplicate within the variant's target list (variant inherits original's list).
        const exists = words.some(w =>
          w.list === originalWord.list &&
          getLettersFromText(w.text) === normalizedLetters
        )
        if (exists) return

        // Use existing groupId or create new one
        let groupId = originalWord.groupId
        let updatedWords = words

        if (!groupId) {
          // Create new group and assign it to the original word
          groupId = generateId()
          updatedWords = words.map(w =>
            w.id === wordId ? { ...w, groupId } : w
          )
        }

        // Create the variant with the same list and groupId
        const variant: Word = {
          id: generateId(),
          text: variantText.toUpperCase(),
          list: originalWord.list,
          groupId
        }

        set({ words: [...updatedWords, variant] })
      },

      updateWord: (id, updates) => {
        set(state => ({
          words: state.words.map(w =>
            w.id === id
              ? { ...w, ...updates, text: updates.text?.toUpperCase() ?? w.text }
              : w
          )
        }))
      },

      assignWordToList: (id, list) => {
        set(state => ({
          words: state.words.map(w =>
            w.id === id ? { ...w, list } : w
          )
        }))
      },

      deleteWord: (id) => {
        set(state => ({
          words: state.words.filter(w => w.id !== id),
          // Also delete any pairings involving this word
          pairings: state.pairings.filter(p => p.wordAId !== id && p.wordBId !== id),
          selectedWordId: state.selectedWordId === id ? null : state.selectedWordId
        }))
      },

      createWordGroup: (wordIds) => {
        // Groups are per-list: split inputs by list so a single call can produce
        // multiple groups (one per list). Singleton buckets get no group.
        const idSet = new Set(wordIds)
        let primaryGroupId = ''
        set(state => {
          const wordsToGroup = state.words.filter(w => idSet.has(w.id))
          const buckets = new Map<string, string[]>()
          for (const word of wordsToGroup) {
            const key = word.list ?? 'unsorted'
            const bucket = buckets.get(key)
            if (bucket) bucket.push(word.id)
            else buckets.set(key, [word.id])
          }
          const groupIdByWordId = new Map<string, string>()
          for (const idsInBucket of buckets.values()) {
            if (idsInBucket.length < 2) continue
            const groupId = generateId()
            if (!primaryGroupId) primaryGroupId = groupId
            for (const id of idsInBucket) groupIdByWordId.set(id, groupId)
          }
          return {
            words: state.words.map(w => {
              const newGroupId = groupIdByWordId.get(w.id)
              return newGroupId ? { ...w, groupId: newGroupId } : w
            })
          }
        })
        return primaryGroupId
      },

      removeFromGroup: (wordId) => {
        const { words } = get()
        const word = words.find(w => w.id === wordId)
        if (!word || !word.groupId) return

        const groupId = word.groupId
        const groupMembers = words.filter(w => w.groupId === groupId)

        // If only 2 members, dissolve the group entirely
        if (groupMembers.length <= 2) {
          set(state => ({
            words: state.words.map(w =>
              w.groupId === groupId ? { ...w, groupId: undefined } : w
            )
          }))
        } else {
          // Just remove this word from the group
          set(state => ({
            words: state.words.map(w =>
              w.id === wordId ? { ...w, groupId: undefined } : w
            )
          }))
        }
      },

      markAsTwins: (wordIdA, wordIdB) => {
        // Twins link a list-A group and a list-B group as "the same word".
        // Used for grid-suggestion dedup so placing one excludes the other.
        // Each word's whole group (or just itself if ungrouped) is marked.
        const { words } = get()
        const wordA = words.find(w => w.id === wordIdA)
        const wordB = words.find(w => w.id === wordIdB)
        if (!wordA || !wordB) return
        if (wordA.list && wordB.list && wordA.list === wordB.list) return

        const collect = (word: Word): string[] =>
          word.groupId
            ? words.filter(w => w.groupId === word.groupId).map(w => w.id)
            : [word.id]

        // Reuse an existing twinGroupId if one side already has one; else create new.
        const twinGroupId = wordA.twinGroupId ?? wordB.twinGroupId ?? generateId()
        const idsToMark = new Set<string>([...collect(wordA), ...collect(wordB)])

        // Pull in any other words already linked to either side's twinGroupId
        // so the twin set stays connected after merges.
        for (const w of words) {
          if (w.twinGroupId && (w.twinGroupId === wordA.twinGroupId || w.twinGroupId === wordB.twinGroupId)) {
            idsToMark.add(w.id)
          }
        }

        set(state => ({
          words: state.words.map(w =>
            idsToMark.has(w.id) ? { ...w, twinGroupId } : w
          )
        }))
      },

      unmarkTwin: (wordId) => {
        const { words } = get()
        const word = words.find(w => w.id === wordId)
        if (!word?.twinGroupId) return
        const twinGroupId = word.twinGroupId

        // Remove the twinGroupId from this word's whole group only — the other
        // side's group stays linked among itself but loses this side.
        const sameGroupIds = new Set(
          word.groupId
            ? words.filter(w => w.groupId === word.groupId).map(w => w.id)
            : [word.id]
        )

        set(state => ({
          words: state.words.map(w => {
            if (!sameGroupIds.has(w.id)) return w
            const next: Word = { ...w }
            delete next.twinGroupId
            return next
          })
        }))

        // If the other side now has only one group of twins (i.e. nobody to twin
        // with), clear its twinGroupId too so we don't leave dangling markers.
        const remaining = get().words.filter(w => w.twinGroupId === twinGroupId)
        const remainingLists = new Set(remaining.map(w => w.list ?? 'unsorted'))
        if (remainingLists.size <= 1) {
          set(state => ({
            words: state.words.map(w => {
              if (w.twinGroupId !== twinGroupId) return w
              const next: Word = { ...w }
              delete next.twinGroupId
              return next
            })
          }))
        }
      },

      // Pairing Actions
      getOrCreatePairing: (wordAId, wordBId) => {
        const { words, pairings } = get()
        const wordA = words.find(w => w.id === wordAId)
        const wordB = words.find(w => w.id === wordBId)

        if (!wordA || !wordB) {
          return null
        }

        // Validate same length (required for Schrödinger crossword)
        const lenA = getLetters(wordA).length
        const lenB = getLetters(wordB).length
        if (lenA !== lenB) {
          return null
        }

        // Check if pairing already exists
        const existing = pairings.find(p =>
          (p.wordAId === wordAId && p.wordBId === wordBId) ||
          (p.wordAId === wordBId && p.wordBId === wordAId)
        )

        if (existing) return existing

        // Create new pairing (returns null if words aren't from different lists)
        const newPairing = createPairing(wordA, wordB)
        if (!newPairing) return null
        set(state => ({ pairings: [...state.pairings, newPairing] }))
        return newPairing
      },

      updatePairingStatus: (id, status) => {
        // Setting status to 'none' is "untrack this pair" — the record goes
        // away, the pair returns to ephemeral status (no record at all).
        // Equivalent to deletePairing for any caller setting 'none'.
        if (status === 'none') {
          set(state => ({
            pairings: state.pairings.filter(p => p.id !== id),
            selectedPairingId: state.selectedPairingId === id ? null : state.selectedPairingId,
          }))
          return
        }
        set(state => ({
          pairings: state.pairings.map(p =>
            p.id === id ? { ...p, status } : p
          )
        }))
      },

      updatePairingRating: (id, rating) => {
        // null clears the rating; numeric values set it.
        set(state => ({
          pairings: state.pairings.map(p => {
            if (p.id !== id) return p
            if (rating === null) {
              const next = { ...p }
              delete next.rating
              return next
            }
            return { ...p, rating }
          })
        }))
      },

      updatePairingClue: (id, text, status) => {
        // Empty / whitespace-only text clears the clue entirely. The active
        // clue field becomes undefined; saved drafts are untouched (use
        // deletePairingDraftClue for those).
        const trimmedText = text.trim()
        set(state => ({
          pairings: state.pairings.map(p => {
            if (p.id !== id) return p
            if (!trimmedText) {
              const next = { ...p }
              delete next.clue
              return next
            }
            return { ...p, clue: { text: trimmedText, status, updatedAt: new Date().toISOString() } }
          })
        }))
      },

      addPairingDraftClue: (id, text) => {
        const trimmedText = text.trim()
        if (!trimmedText) return

        const timestamp = new Date().toISOString()
        set(state => ({
          pairings: state.pairings.map(p => {
            if (p.id !== id) return p

            const existingDrafts = p.clueDrafts ?? []
            const existingDraft = existingDrafts.find(draft =>
              draft.text.trim().toLowerCase() === trimmedText.toLowerCase()
            )
            const clue = existingDraft
              ? { ...existingDraft, text: trimmedText, status: 'draft' as const, updatedAt: timestamp }
              : { id: generateId(), text: trimmedText, status: 'draft' as const, createdAt: timestamp, updatedAt: timestamp }
            const clueDrafts = existingDraft
              ? existingDrafts.map(draft => draft === existingDraft ? clue : draft)
              : [...existingDrafts, clue]

            return { ...p, clue, clueDrafts }
          })
        }))
      },

      deletePairingDraftClue: (pairingId, clueId) => {
        set(state => ({
          pairings: state.pairings.map(p => {
            if (p.id !== pairingId) return p

            const clueDrafts = (p.clueDrafts ?? []).filter(clue => clue.id !== clueId)
            const clue = p.clue?.id === clueId ? clueDrafts.at(-1) : p.clue
            return { ...p, clue, clueDrafts }
          })
        }))
      },

      deletePairing: (id) => {
        set(state => ({
          pairings: state.pairings.filter(p => p.id !== id),
          selectedPairingId: state.selectedPairingId === id ? null : state.selectedPairingId
        }))
      },

      // Tag actions. setWordTags / setPairingTags also auto-register any new
      // labels into the tag registry so the filter chip bar discovers them
      // without a separate "create tag" flow. Empty / whitespace labels are
      // dropped on the way in.
      setWordTags: (wordId, tags) => {
        const cleaned = normalizeTagInput(tags)
        set(state => ({
          words: state.words.map(w => (w.id === wordId ? { ...w, tags: cleaned } : w)),
          tags: mergeRegistry(state.tags, cleaned),
        }))
      },

      setPairingTags: (pairingId, tags) => {
        const cleaned = normalizeTagInput(tags)
        set(state => ({
          pairings: state.pairings.map(p => (p.id === pairingId ? { ...p, tags: cleaned } : p)),
          tags: mergeRegistry(state.tags, cleaned),
        }))
      },

      registerTag: (label) => {
        const cleaned = label.trim()
        if (!cleaned) return
        set(state => ({ tags: mergeRegistry(state.tags, [cleaned]) }))
      },

      renameTag: (oldLabel, newLabel) => {
        const target = newLabel.trim()
        if (!target || oldLabel === target) return
        set(state => ({
          words: state.words.map(w =>
            w.tags?.includes(oldLabel)
              ? { ...w, tags: replaceLabel(w.tags, oldLabel, target) }
              : w,
          ),
          pairings: state.pairings.map(p =>
            p.tags?.includes(oldLabel)
              ? { ...p, tags: replaceLabel(p.tags, oldLabel, target) }
              : p,
          ),
          tags: mergeRegistry(
            state.tags.filter(t => t !== oldLabel),
            [target],
          ),
        }))
      },

      deleteTag: (label) => {
        set(state => ({
          words: state.words.map(w =>
            w.tags?.includes(label) ? { ...w, tags: w.tags.filter(t => t !== label) } : w,
          ),
          pairings: state.pairings.map(p =>
            p.tags?.includes(label) ? { ...p, tags: p.tags.filter(t => t !== label) } : p,
          ),
          tags: state.tags.filter(t => t !== label),
        }))
      },

      // UI Actions
      setSelectedWord: (id) => set({ selectedWordId: id }),
      setSelectedPairing: (id) => set({ selectedPairingId: id }),
      setActiveView: (view) => set({ activeView: view }),

      setFilters: (newFilters) => {
        set(state => ({
          filters: { ...state.filters, ...newFilters }
        }))
      },

      resetFilters: () => set({ filters: defaultFilters }),

      setListName: (list, name) => {
        set(state => ({
          listNames: { ...state.listNames, [list]: name }
        }))
      },

      // Constructor Actions
      undo: () => {
        const { historyPast, historyFuture } = get()
        const previous = historyPast[historyPast.length - 1]
        if (!previous) return

        const current = createHistorySnapshot()
        const nextPast = historyPast.slice(0, -1)
        const nextFuture = [current, ...historyFuture].slice(0, MAX_HISTORY_ENTRIES)

        set(state => ({
          words: previous.words,
          pairings: previous.pairings,
          listNames: previous.listNames,
          constructorGrid: previous.constructorGrid,
          drafts: previous.drafts,
          activeDraftId: previous.activeDraftId,
          scratchpadGrid: previous.scratchpadGrid ?? null,
          gridVersion: state.gridVersion + 1,
          selectedWordId: null,
          selectedPairingId: null,
          selectedGridCell: null,
          historyPast: nextPast,
          historyFuture: nextFuture,
          canUndo: nextPast.length > 0,
          canRedo: nextFuture.length > 0
        }))

      },

      redo: () => {
        const { historyPast, historyFuture } = get()
        const next = historyFuture[0]
        if (!next) return

        const current = createHistorySnapshot()
        const nextPast = [...historyPast, current].slice(-MAX_HISTORY_ENTRIES)
        const nextFuture = historyFuture.slice(1)

        set(state => ({
          words: next.words,
          pairings: next.pairings,
          listNames: next.listNames,
          constructorGrid: next.constructorGrid,
          drafts: next.drafts,
          activeDraftId: next.activeDraftId,
          scratchpadGrid: next.scratchpadGrid ?? null,
          gridVersion: state.gridVersion + 1,
          selectedWordId: null,
          selectedPairingId: null,
          selectedGridCell: null,
          historyPast: nextPast,
          historyFuture: nextFuture,
          canUndo: nextPast.length > 0,
          canRedo: nextFuture.length > 0
        }))

      },

      createConstructorGrid: (width, height) => {
        setGrid(createGrid(width, height), { recordHistory: true })
      },

      placeOnGrid: (placement) => {
        const { constructorGrid } = get()
        if (!constructorGrid) {
          return { success: false, error: 'No grid created' }
        }

        const result = placePair(constructorGrid, placement)
        if (!result.success) {
          return { success: false, error: result.error }
        }

        setGrid(result.grid, { recordHistory: true })
        return { success: true, placementId: result.placementId }
      },

      placeManyOnGrid: (placements) => {
        const { constructorGrid } = get()
        if (!constructorGrid) return { success: false, error: 'No grid created' }
        if (placements.length === 0) return { success: true, placementIds: [] }

        const result = validatePlacementBatch(constructorGrid, placements)
        if (!result.valid) {
          const first = result.reasons[0]
          return { success: false, error: first ? `${first.reason}${first.detail ? ` (${first.detail})` : ''}` : 'invalid' }
        }
        setGrid(result.grid, { recordHistory: true })
        return { success: true, placementIds: result.placementIds }
      },

      removeFromGrid: (placementId) => {
        const { constructorGrid } = get()
        if (!constructorGrid) return
        setGrid(removePlacement(constructorGrid, placementId), { recordHistory: true })
      },

      moveGridPlacement: (placementId, newRow, newCol) => {
        const { constructorGrid } = get()
        if (!constructorGrid) return
        setGrid(movePlacement(constructorGrid, placementId, newRow, newCol), { recordHistory: true })
      },

      moveGridPlacements: (updates) => {
        const { constructorGrid } = get()
        if (!constructorGrid || updates.length === 0) return
        setGrid(movePlacements(constructorGrid, updates), { recordHistory: true })
      },

      removeManyFromGrid: (placementIds) => {
        const { constructorGrid } = get()
        if (!constructorGrid || placementIds.length === 0) return
        setGrid(removePlacements(constructorGrid, placementIds), { recordHistory: true })
      },

      sendPlacementsToScratchpad: (placementIds) => {
        const { constructorGrid, scratchpadGrid, activeDraftId, drafts, gridVersion } = get()
        if (!constructorGrid) return { success: false, error: 'No grid' }
        if (placementIds.length === 0) return { success: false, error: 'No placements selected' }

        const idSet = new Set(placementIds)
        const selected = constructorGrid.placements.filter(p => idSet.has(p.id))
        if (selected.length === 0) return { success: false, error: 'No matching placements' }

        // Normalize coords so the group lands at (0,0) on the scratchpad while
        // preserving its internal layout.
        const fragment = fragmentFromPlacements(selected, '__transfer')
        if (!fragment) return { success: false, error: 'Could not normalize placements' }

        // Pick / resize a scratchpad that fits. Default to a workable canvas
        // size (~30x30) to leave the user room to rearrange.
        const minWidth = Math.max(fragment.width, 30)
        const minHeight = Math.max(fragment.height, 30)
        let nextScratchpad: ConstructorGrid
        if (!scratchpadGrid) {
          nextScratchpad = createGrid(minWidth, minHeight)
        } else if (scratchpadGrid.width < fragment.width || scratchpadGrid.height < fragment.height) {
          nextScratchpad = resizeGrid(
            scratchpadGrid,
            Math.max(scratchpadGrid.width, minWidth),
            Math.max(scratchpadGrid.height, minHeight),
            0,
            0,
          )
        } else {
          nextScratchpad = scratchpadGrid
        }

        // Place each fragment piece at its normalized position. New placement
        // ids — the originals belong to the main grid (or, after this action,
        // to history).
        for (const p of fragment.placements) {
          const result = placePair(nextScratchpad, {
            pairingId: p.pairingId,
            wordAText: p.wordAText,
            wordBText: p.wordBText,
            row: p.row,
            col: p.col,
            direction: p.direction,
            isTempA: p.isTempA,
            isTempB: p.isTempB,
            priority: p.priority,
            id: generateId(),
          })
          if (!result.success) return { success: false, error: result.error }
          nextScratchpad = result.grid
        }

        const nextConstructorGrid = removePlacements(constructorGrid, placementIds)

        // Single atomic update — push history once, then write both grids.
        pushHistory()
        const updatedAt = new Date().toISOString()
        set({
          constructorGrid: nextConstructorGrid,
          scratchpadGrid: nextScratchpad,
          gridVersion: gridVersion + 1,
          ...(activeDraftId ? {
            drafts: drafts.map(d =>
              d.id === activeDraftId ? { ...d, grid: nextConstructorGrid, updatedAt } : d,
            ),
          } : {}),
        })
        return { success: true }
      },

      rotateGridPlacement: (placementId) => {
        const { constructorGrid } = get()
        if (!constructorGrid) return
        setGrid(rotatePlacement(constructorGrid, placementId), { recordHistory: true })
      },

      resizeConstructorGrid: (width, height, rowOffset = 0, colOffset = 0) => {
        const { constructorGrid } = get()
        if (!constructorGrid) {
          setGrid(createGrid(width, height), { recordHistory: true })
        } else {
          setGrid(resizeGrid(constructorGrid, width, height, rowOffset, colOffset), { recordHistory: true })
        }
      },

      clearConstructorGrid: () => {
        const { constructorGrid } = get()
        if (!constructorGrid) return
        setGrid(clearGrid(constructorGrid), { recordHistory: true })
      },

      toggleGridCellBlocked: (row, col) => {
        const { constructorGrid } = get()
        if (!constructorGrid) return
        const newGrid = toggleCellBlocked(constructorGrid, row, col)
        if (newGrid === constructorGrid) return
        setGrid(newGrid, { recordHistory: true })
      },

      toggleGridCellsBlocked: (cells) => {
        const { constructorGrid } = get()
        if (!constructorGrid) return
        const newGrid = toggleCellsBlocked(constructorGrid, cells)
        if (newGrid === constructorGrid) return
        setGrid(newGrid, { recordHistory: true })
      },

      setConstructorViewMode: (mode) => {
        set({ constructorViewMode: mode })
      },

      setSelectedGridCell: (cell) => {
        set({ selectedGridCell: cell })
      },

      setDraggedPairingId: (id) => {
        set({ draggedPairingId: id })
      },

      saveManualEntry: (wordAText, wordBText, clue) => {
        const { words, pairings } = get()
        const normalizedA = wordAText.toUpperCase().trim()
        const normalizedB = wordBText.toUpperCase().trim()

        // Validate same length
        const lettersA = normalizedA.replace(/\s/g, '')
        const lettersB = normalizedB.replace(/\s/g, '')
        if (lettersA.length !== lettersB.length) {
          return null
        }

        let historyRecorded = false
        const ensureHistory = () => {
          if (historyRecorded) return
          pushHistory()
          historyRecorded = true
        }

        // Find or create word A
        let wordA = words.find(w =>
          w.list === 'A' && getLettersFromText(w.text) === lettersA
        )
        if (!wordA) {
          wordA = {
            id: generateId(),
            text: normalizedA,
            list: 'A'
          }
          ensureHistory()
          set(state => ({ words: [...state.words, wordA!] }))
        }

        // Find or create word B
        let wordB = words.find(w =>
          w.list === 'B' && getLettersFromText(w.text) === lettersB
        )
        if (!wordB) {
          wordB = {
            id: generateId(),
            text: normalizedB,
            list: 'B'
          }
          ensureHistory()
          set(state => ({ words: [...state.words, wordB!] }))
        }

        // Find or create pairing
        let pairing = pairings.find(p =>
          p.wordAId === wordA!.id && p.wordBId === wordB!.id
        )
        if (!pairing) {
          const timestamp = new Date().toISOString()
          const draftClue = clue
            ? { id: generateId(), text: clue.trim(), status: 'draft' as const, createdAt: timestamp, updatedAt: timestamp }
            : undefined
          pairing = {
            id: generateId(),
            wordAId: wordA.id,
            wordBId: wordB.id,
            status: 'draft',
            clue: draftClue,
            clueDrafts: draftClue ? [draftClue] : undefined
          }
          ensureHistory()
          set(state => ({ pairings: [...state.pairings, pairing!] }))
        } else if (clue && !pairing.clue) {
          // Update existing pairing with clue
          const timestamp = new Date().toISOString()
          const draftClue = { id: generateId(), text: clue.trim(), status: 'draft' as const, createdAt: timestamp, updatedAt: timestamp }
          ensureHistory()
          set(state => ({
            pairings: state.pairings.map(p =>
              p.id === pairing!.id ? { ...p, clue: draftClue, clueDrafts: [...(p.clueDrafts ?? []), draftClue] } : p
            )
          }))
        }

        return pairing
      },

      updateGridPlacementLetter: (placementId, letterIndex, newLetter, side) => {
        const { constructorGrid } = get()
        if (!constructorGrid) return

        const newGrid = updatePlacementLetter(constructorGrid, placementId, letterIndex, newLetter, side)
        setGrid(newGrid, { recordHistory: true })
      },

      saveTempWord: (placementId, side) => {
        const { constructorGrid, words, pairings } = get()
        if (!constructorGrid) return

        const placement = constructorGrid.placements.find(p => p.id === placementId)
        if (!placement) return

        if ((side === 'A' && !placement.isTempA) || (side === 'B' && !placement.isTempB)) {
          return
        }

        const wordText = side === 'A' ? placement.wordAText : placement.wordBText
        const normalizedText = wordText.toUpperCase().trim()
        const letters = normalizedText.replace(/\s/g, '')

        // Don't save placeholder words
        if (letters.includes('□')) return

        pushHistory()

        // Check if word already exists in the list
        const list: ListSide = side
        let existingWord = words.find(w =>
          w.list === list && getLettersFromText(w.text) === letters
        )

        // Create word if it doesn't exist
        if (!existingWord) {
          existingWord = {
            id: generateId(),
            text: normalizedText,
            list
          }
          set(state => ({ words: [...state.words, existingWord!] }))
        }

        // Get the other side's word
        const otherWordText = side === 'A' ? placement.wordBText : placement.wordAText
        const otherLetters = getLettersFromText(otherWordText)
        const otherList: ListSide = side === 'A' ? 'B' : 'A'
        const otherIsTempFlag = side === 'A' ? 'isTempB' : 'isTempA'
        const otherIsTemp = placement[otherIsTempFlag]

        // Check if the other side has a saved word (not temp and not placeholder)
        let otherWord: Word | undefined
        if (!otherIsTemp && !otherLetters.includes('□')) {
          otherWord = words.find(w =>
            w.list === otherList && getLettersFromText(w.text) === otherLetters
          )
        }

        // If both sides now have saved words, create or find pairing
        let newPairingId = placement.pairingId
        if (otherWord && existingWord) {
          const wordAId = side === 'A' ? existingWord.id : otherWord.id
          const wordBId = side === 'A' ? otherWord.id : existingWord.id

          // Check for existing pairing
          let pairing = pairings.find(p =>
            p.wordAId === wordAId && p.wordBId === wordBId
          )

          if (!pairing) {
            pairing = {
              id: generateId(),
              wordAId,
              wordBId,
              status: 'draft',
              clue: undefined
            }
            set(state => ({ pairings: [...state.pairings, pairing!] }))
          }
          newPairingId = pairing.id
        }

        // Update the placement to mark this side as no longer temp
        const updatedPlacement = {
          ...placement,
          pairingId: newPairingId,
          isTempA: side === 'A' ? false : placement.isTempA,
          isTempB: side === 'B' ? false : placement.isTempB
        }

        const newGrid: ConstructorGrid = {
          ...constructorGrid,
          placements: constructorGrid.placements.map(p =>
            p.id === placementId ? updatedPlacement : p
          )
        }
        setGrid(newGrid)
      },

      setPlacementPriority: (placementId, priority) => {
        const { constructorGrid } = get()
        if (!constructorGrid) return
        const currentPlacement = constructorGrid.placements.find(p => p.id === placementId)
        if (!currentPlacement || currentPlacement.priority === priority) return

        const newGrid: ConstructorGrid = {
          ...constructorGrid,
          placements: constructorGrid.placements.map(p =>
            p.id === placementId ? { ...p, priority } : p
          )
        }
        setGrid(newGrid, { recordHistory: true })
      },

      // Draft Actions
      createDraft: (name) => {
        const { drafts, constructorGrid } = get()
        const draftNumber = drafts.length + 1
        const draftName = name || `Draft ${draftNumber}`
        const newDraft: GridDraft = {
          id: generateId(),
          name: draftName,
          grid: createGrid(15, 15), // Default size
          updatedAt: new Date().toISOString()
        }

        // If there's an active draft, save the current grid first
        const { activeDraftId } = get()
        let updatedDrafts = drafts
        if (activeDraftId && constructorGrid) {
          updatedDrafts = drafts.map(d =>
            d.id === activeDraftId
              ? { ...d, grid: constructorGrid, updatedAt: new Date().toISOString() }
              : d
          )
        }

        if (drafts.length > 0 || constructorGrid !== null) {
          pushHistory()
        }
        set({
          drafts: [...updatedDrafts, newDraft],
          activeDraftId: newDraft.id,
          constructorGrid: newDraft.grid
        })
      },

      switchDraft: (draftId) => {
        const { drafts, constructorGrid, activeDraftId } = get()
        const targetDraft = drafts.find(d => d.id === draftId)
        if (!targetDraft) return

        // Save current grid to active draft before switching
        let updatedDrafts = drafts
        if (activeDraftId && constructorGrid) {
          updatedDrafts = drafts.map(d =>
            d.id === activeDraftId
              ? { ...d, grid: constructorGrid, updatedAt: new Date().toISOString() }
              : d
          )
        }

        set({
          drafts: updatedDrafts,
          activeDraftId: draftId,
          constructorGrid: targetDraft.grid,
          selectedGridCell: null
        })

      },

      renameDraft: (draftId, name) => {
        set(state => ({
          drafts: state.drafts.map(d =>
            d.id === draftId ? { ...d, name } : d
          )
        }))
      },

      deleteDraft: (draftId) => {
        const { drafts, activeDraftId } = get()
        if (!drafts.some(d => d.id === draftId)) return
        const remainingDrafts = drafts.filter(d => d.id !== draftId)

        pushHistory()
        // If deleting the active draft, switch to another one
        if (activeDraftId === draftId) {
          const newActiveDraft = remainingDrafts[0] || null
          set({
            drafts: remainingDrafts,
            activeDraftId: newActiveDraft?.id || null,
            constructorGrid: newActiveDraft?.grid || null
          })
        } else {
          set({ drafts: remainingDrafts })
        }
      },

      duplicateDraft: (draftId) => {
        const { drafts, constructorGrid, activeDraftId } = get()
        const sourceDraft = drafts.find(d => d.id === draftId)
        if (!sourceDraft) return

        // Use current grid if duplicating active draft
        const gridToCopy = draftId === activeDraftId && constructorGrid
          ? constructorGrid
          : sourceDraft.grid

        const newDraft: GridDraft = {
          id: generateId(),
          name: `${sourceDraft.name} (copy)`,
          grid: JSON.parse(JSON.stringify(gridToCopy)), // Deep clone
          updatedAt: new Date().toISOString()
        }

        pushHistory()
        set({ drafts: [...drafts, newDraft] })
      },

      // Scratchpad
      addScratchpadEntry: () => {
        set(state => ({
          scratchpadEntries: [...state.scratchpadEntries, {
            id: generateId(),
            wordA: '',
            wordB: '',
            clue: ''
          }]
        }))
      },

      updateScratchpadEntry: (id, updates) => {
        set(state => ({
          scratchpadEntries: state.scratchpadEntries.map(e =>
            e.id === id ? { ...e, ...updates } : e
          )
        }))
      },

      removeScratchpadEntry: (id) => {
        set(state => ({
          scratchpadEntries: state.scratchpadEntries.filter(e => e.id !== id)
        }))
      },

      createScratchpadGrid: () => {
        set(state => ({
          scratchpadGrid: state.scratchpadGrid ?? createGrid(1, 1)
        }))
      },

      placeOnScratchpad: (placement) => {
        const scratchpadGrid = get().scratchpadGrid ?? createGrid(1, 1)
        const result = placePair(scratchpadGrid, placement)
        if (!result.success) {
          return { success: false, error: result.error }
        }
        setScratchpadGrid(result.grid, { recordHistory: true })
        return { success: true, placementId: result.placementId }
      },

      removeFromScratchpad: (placementId) => {
        const { scratchpadGrid } = get()
        if (!scratchpadGrid) return
        setScratchpadGrid(removePlacement(scratchpadGrid, placementId), { recordHistory: true })
      },

      moveScratchpadPlacement: (placementId, newRow, newCol) => {
        const { scratchpadGrid } = get()
        if (!scratchpadGrid) return
        setScratchpadGrid(movePlacement(scratchpadGrid, placementId, newRow, newCol), { recordHistory: true })
      },

      moveScratchpadPlacements: (updates) => {
        const { scratchpadGrid } = get()
        if (!scratchpadGrid || updates.length === 0) return
        setScratchpadGrid(movePlacements(scratchpadGrid, updates), { recordHistory: true })
      },

      removeManyFromScratchpad: (placementIds) => {
        const { scratchpadGrid } = get()
        if (!scratchpadGrid || placementIds.length === 0) return
        setScratchpadGrid(removePlacements(scratchpadGrid, placementIds), { recordHistory: true })
      },

      rotateScratchpadPlacement: (placementId) => {
        const { scratchpadGrid } = get()
        if (!scratchpadGrid) return
        setScratchpadGrid(rotatePlacement(scratchpadGrid, placementId), { recordHistory: true })
      },

      clearScratchpadGrid: () => {
        setScratchpadGrid(createGrid(1, 1), { recordHistory: true })
      },

      updateScratchpadPlacementLetter: (placementId, letterIndex, newLetter, side) => {
        const { scratchpadGrid } = get()
        if (!scratchpadGrid) return
        setScratchpadGrid(updatePlacementLetter(scratchpadGrid, placementId, letterIndex, newLetter, side), { recordHistory: true })
      },

      // Fragment Actions
      saveFragment: (placements, name) => {
        const fragment = fragmentFromPlacements(placements, name.trim() || 'Untitled fragment')
        if (!fragment) return null
        set(state => ({ fragments: [...state.fragments, fragment] }))
        return fragment
      },

      deleteFragment: (id) => {
        set(state => ({ fragments: state.fragments.filter(f => f.id !== id) }))
      },

      renameFragment: (id, name) => {
        const trimmed = name.trim()
        if (!trimmed) return
        const updatedAt = new Date().toISOString()
        set(state => ({
          fragments: state.fragments.map(f =>
            f.id === id ? { ...f, name: trimmed, updatedAt } : f
          )
        }))
      },

      applyFragmentToGrid: (fragmentId, rowOffset, colOffset) => {
        const { fragments, constructorGrid } = get()
        if (!constructorGrid) return { success: false, error: 'No active grid' }
        const fragment = fragments.find(f => f.id === fragmentId)
        if (!fragment) return { success: false, error: 'Fragment not found' }
        const result = tryApplyFragment(constructorGrid, fragment, rowOffset, colOffset)
        if (!result.success) return { success: false, error: result.error }
        setGrid(result.grid, { recordHistory: true })
        return { success: true }
      },

      // Derived state: pairing IDs fully placed inside the grid
      getInsidePairingIds: () => {
        const { constructorGrid } = get()
        if (!constructorGrid) return new Set<string>()

        const ids = new Set<string>()
        for (const placement of constructorGrid.placements) {
          if (!placement.pairingId) continue
          const wordLength = getLettersFromText(placement.wordAText).length
          let allInside = true
          for (let i = 0; i < wordLength; i++) {
            const row = placement.direction === 'down' ? placement.row + i : placement.row
            const col = placement.direction === 'across' ? placement.col + i : placement.col
            if (row < 0 || row >= constructorGrid.height || col < 0 || col >= constructorGrid.width) {
              allInside = false
              break
            }
          }
          if (allInside) ids.add(placement.pairingId)
        }
        return ids
      },

      // Search/Filter
      getFilteredPairings: () => {
        const { pairings, filters } = get()
        let result = pairings

        if (filters.pairingStatuses.length > 0) {
          result = filterByPairingStatus(result, filters.pairingStatuses)
        }

        if (filters.clueStatuses.length > 0) {
          result = filterByClueStatus(result, filters.clueStatuses)
        }

        if (filters.searchQuery) {
          result = searchByClueText(result, filters.searchQuery)
        }

        return result
      },

      searchWords: (pattern) => {
        const { words } = get()
        return searchWordsWithWildcard(words, pattern)
      },

      findPairingsByLetterPositions: (positions) => {
        const { pairings, words } = get()
        return findByLetterPositions(pairings, words, positions)
      },

      // Export/Import
      exportData: () => {
        const { words, pairings, listNames, drafts, activeDraftId, scratchpadEntries, scratchpadGrid, fragments } = get()
        return { words, pairings, listNames, drafts, activeDraftId, scratchpadEntries, scratchpadGrid, fragments }
      },

      importData: (data) => {
        const cleanData = sanitizeCrosswordData(data)
        if (!cleanData) return

        const words = cleanData.words
        const pairings = cleanData.pairings
        const drafts = cleanData.drafts ?? []
        const scratchpadEntries = cleanData.scratchpadEntries ?? []
        const scratchpadGrid = cleanData.scratchpadGrid ?? null
        const fragments = cleanData.fragments ?? []

        // Set constructorGrid from active draft if it exists
        const activeDraft = cleanData.activeDraftId
          ? drafts.find(d => d.id === cleanData.activeDraftId)
          : drafts[0] ?? null
        const constructorGrid = activeDraft?.grid ?? null

        set({
          words,
          pairings,
          listNames: cleanData.listNames ?? { A: 'List A', B: 'List B' },
          drafts,
          activeDraftId: activeDraft?.id ?? null,
          constructorGrid,
          scratchpadEntries,
          scratchpadGrid,
          fragments,
          selectedWordId: null,
          selectedPairingId: null,
          selectedGridCell: null,
          historyPast: [],
          historyFuture: [],
          canUndo: false,
          canRedo: false
        })

      },

      mergeData: (data) => {
        const cleanData = sanitizeCrosswordData(data)
        if (!cleanData) return

        pushHistory()
        const { words: existingWords, pairings: existingPairings } = get()

        // Build a map of existing words by their letters+list for deduplication
        const existingWordKeys = new Map<string, string>() // "letters:list" -> id
        for (const word of existingWords) {
          const key = `${getLettersFromText(word.text)}:${word.list || 'none'}`
          existingWordKeys.set(key, word.id)
        }

        // Map imported word IDs to existing or new IDs
        const wordIdMap = new Map<string, string>() // old id -> new/existing id
        const newWords: Word[] = []

        for (const word of cleanData.words) {
          const key = `${getLettersFromText(word.text)}:${word.list || 'none'}`

          if (existingWordKeys.has(key)) {
            // Word already exists, map to existing ID
            wordIdMap.set(word.id, existingWordKeys.get(key)!)
          } else {
            // New word, generate new ID and add
            const newId = generateId()
            wordIdMap.set(word.id, newId)
            existingWordKeys.set(key, newId)
            newWords.push({
              ...word,
              id: newId
            })
          }
        }

        // Build set of existing pairings for deduplication
        const existingPairingKeys = new Set<string>()
        for (const pairing of existingPairings) {
          existingPairingKeys.add(`${pairing.wordAId}:${pairing.wordBId}`)
        }

        // Add new pairings, mapping word IDs
        const allWordsAfterMerge = [...existingWords, ...newWords]
        const newPairings: Pairing[] = []
        for (const pairing of cleanData.pairings) {
          const newWordAId = wordIdMap.get(pairing.wordAId)
          const newWordBId = wordIdMap.get(pairing.wordBId)

          // Skip if either word wasn't in the import
          if (!newWordAId || !newWordBId) continue

          // Validate same length (required for Schrödinger crossword)
          const wordA = allWordsAfterMerge.find(w => w.id === newWordAId)
          const wordB = allWordsAfterMerge.find(w => w.id === newWordBId)
          if (!wordA || !wordB) continue
          if (getLetters(wordA).length !== getLetters(wordB).length) continue

          const key = `${newWordAId}:${newWordBId}`
          if (!existingPairingKeys.has(key)) {
            existingPairingKeys.add(key)
            newPairings.push({
              ...pairing,
              id: generateId(),
              wordAId: newWordAId,
              wordBId: newWordBId
            })
          }
        }

        set({
          words: [...existingWords, ...newWords],
          pairings: [...existingPairings, ...newPairings]
          // Keep existing listNames - don't overwrite
        })
      },

      clearAllData: () => {
        pushHistory()
        set({
          words: [],
          pairings: [],
          listNames: { A: 'List A', B: 'List B' },
          selectedWordId: null,
          selectedPairingId: null,
          selectedGridCell: null,
          constructorGrid: null,
          drafts: [],
          activeDraftId: null,
          scratchpadEntries: [],
          scratchpadGrid: null,
          fragments: [],
          tags: [],
          filters: defaultFilters
        })
      }
    })},
    {
      name: 'crossword-storage',
      partialize: (state) => ({
        words: state.words,
        pairings: state.pairings,
        listNames: state.listNames,
        constructorGrid: state.constructorGrid,
        constructorViewMode: state.constructorViewMode,
        drafts: state.drafts,
        activeDraftId: state.activeDraftId,
        scratchpadEntries: state.scratchpadEntries,
        scratchpadGrid: state.scratchpadGrid,
        fragments: state.fragments,
        tags: state.tags,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return
        // Groups used to span lists; split them on first load so existing data
        // matches the per-list invariant.
        state.words = splitCrossListGroups(state.words)

        // Migration: status redesign.
        //   - 'used' was previously a stored status; now derived from grid
        //     placements. Any persisted 'used' becomes 'approved' (most likely
        //     intent — placed pairs were typically vetted first).
        //   - 'favourite' had a brief stint as a tag, then a status. It's
        //     gone for good now — replaced by the 5/great rating. Pairings
        //     tagged 'favourite' OR with status 'favourite' become approved
        //     with rating 5; the tag is stripped and dropped from the registry.
        //   - Existing 'none' pairings become 'draft'. 'none' is now reserved
        //     for unrecorded combinations (no Pairing record); any record
        //     that exists in the table represents at least a draft-level
        //     commit — "I noticed this pair, want a second look later".
        let pairingsChanged = false
        const migratedPairings = state.pairings.map(p => {
          let next = p
          if ((next.status as string) === 'used') {
            next = { ...next, status: 'approved' as PairingStatus }
            pairingsChanged = true
          }
          // Both legacy favourite paths collapse onto approved + rating 5.
          if ((next.status as string) === 'favourite') {
            next = {
              ...next,
              status: 'approved' as PairingStatus,
              rating: (next.rating ?? 5) as PairingRating,
            }
            pairingsChanged = true
          }
          if (next.tags?.includes('favourite')) {
            next = {
              ...next,
              status: next.status === 'rejected' ? next.status : ('approved' as PairingStatus),
              rating: (next.rating ?? 5) as PairingRating,
              tags: next.tags.filter(t => t !== 'favourite'),
            }
            pairingsChanged = true
          }
          // Quality-tag migration: 'good' → rating 4; 'stretch' / 'bad stretch'
          // → rating 1. Existing rating wins to preserve any explicit user
          // setting. Tags get stripped after the rating assignment.
          const qualityTags = new Set(['good', 'stretch', 'bad stretch'])
          const hasQualityTag = next.tags?.some(t => qualityTags.has(t))
          if (hasQualityTag) {
            let rating = next.rating
            if (rating === undefined) {
              if (next.tags!.includes('good')) rating = 4
              if (next.tags!.includes('stretch') || next.tags!.includes('bad stretch')) {
                // Stretch is more conservative — wins over 'good' when both
                // are present (contradictory tagging defaults to the lower).
                rating = 1
              }
            }
            next = {
              ...next,
              ...(rating !== undefined ? { rating: rating as PairingRating } : {}),
              tags: next.tags!.filter(t => !qualityTags.has(t)),
            }
            pairingsChanged = true
          }
          if (next.status === 'none') {
            next = { ...next, status: 'draft' as PairingStatus }
            pairingsChanged = true
          }
          return next
        })
        if (pairingsChanged) state.pairings = migratedPairings

        // Drop 'favourite' and the quality tags from the registry — they're
        // axes of their own now (favourite = no longer exists; quality tags
        // = rating 1–5). Words still get to keep tags freely.
        const seen = new Set<string>(state.tags ?? [])
        for (const dropped of ['favourite', 'good', 'stretch', 'bad stretch']) seen.delete(dropped)
        for (const p of state.pairings) for (const t of p.tags ?? []) seen.add(t)
        for (const w of state.words) for (const t of w.tags ?? []) seen.add(t)
        for (const dropped of ['favourite', 'good', 'stretch', 'bad stretch']) seen.delete(dropped)
        state.tags = Array.from(seen).sort((a, b) => a.localeCompare(b))
      }
    }
  )
)
